import { useRef, useCallback } from "react";
import type { WSMessage } from "../types";
import { getAuthToken } from "../api/client";

interface WSCallbacks {
  onMessage: (msg: WSMessage) => void;
  onOpen?: () => void;
  onClose?: () => void;
}

export function useWebSocket({ onMessage, onOpen, onClose }: WSCallbacks) {
  const wsRef = useRef<WebSocket | null>(null);
  const callbacksRef = useRef({ onMessage, onOpen, onClose });
  callbacksRef.current = { onMessage, onOpen, onClose };

  const connect = useCallback(async (sessionId: number) => {
    wsRef.current?.close();
    const protocol = window.location.protocol === "https:" ? "wss" : "ws";
    // Browsers can't set headers on a WebSocket, and a ?token= query param leaks the
    // Clerk token into server access logs. So connect without it and send the token
    // as the first frame; the server verifies it before processing any other message.
    const token = await getAuthToken();
    const ws = new WebSocket(`${protocol}://${window.location.host}/ws/session/${sessionId}`);
    wsRef.current = ws;

    ws.onopen = () => {
      ws.send(JSON.stringify({ type: "auth", token }));
      callbacksRef.current.onOpen?.();
    };
    ws.onclose = () => callbacksRef.current.onClose?.();
    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data as string) as WSMessage;
        if (import.meta.env.DEV) console.log("WS ←", msg.type);
        callbacksRef.current.onMessage(msg);
      } catch {
        // ignore malformed frames
      }
    };
  }, []);

  const send = useCallback((msg: object) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      const payload = JSON.stringify(msg);
      if (import.meta.env.DEV) {
        if ((msg as any).type === "audio") {
          console.log("WS → audio", payload.length, "chars");
        } else {
          console.log("WS →", (msg as any).type);
        }
      }
      wsRef.current.send(payload);
    }
  }, []);

  const disconnect = useCallback(() => {
    wsRef.current?.close();
    wsRef.current = null;
  }, []);

  return { connect, send, disconnect };
}
